import React, { createContext, useContext, useRef, useState, useEffect } from 'react';
import { getBewerbungStatus } from '../api';

const Ctx = createContext(null);

export function JobsProvider({ children }) {
  const jobsRef = useRef({});
  const [jobs, setJobs] = useState({});
  const listenersRef = useRef([]);

  const sync = () => setJobs({ ...jobsRef.current });

  const pollJob = async (jobId) => {
    const job = jobsRef.current[jobId];
    if (!job || job.status === 'fertig' || job.status === 'fehler' || job.status === 'warten') return;
    try {
      const { data } = await getBewerbungStatus(jobId);
      const cur = jobsRef.current[jobId];
      if (!cur) return;

      const patch = {};
      if (data.schritt) patch.schritt = data.schritt;
      if (data.schritt_index != null) patch.schritt_index = data.schritt_index;
      if (data.schritt_total != null) patch.schritt_total = data.schritt_total;
      if (data.jobtitel) patch.jobtitel = data.jobtitel;
      if (data.analyse) {
        patch.analyse = data.analyse;
        patch.analyse_struktur = data.analyse_struktur || null;
      }
      if (data.status === 'fertig') {
        patch.status = 'fertig';
      } else if (data.status === 'fehler') {
        patch.status = 'fehler';
        patch.fehler = data.fehler;
      } else if (data.kein_kontingent) {
        patch.status = 'warten';
        patch.kein_kontingent = true;
      } else if (data.analyse && cur.kind === 'analyse') {
        patch.status = 'warten';
      } else if (data.analyse && cur.kind === 'komplett' && cur.status === 'analyse') {
        // analyse done, generation continues → show fit-UI + CV skeleton
        patch.status = 'generiert';
      }

      if (Object.keys(patch).length > 0) {
        jobsRef.current = { ...jobsRef.current, [jobId]: { ...cur, ...patch } };
        sync();
      }
      if (patch.status === 'fertig' || patch.status === 'fehler') {
        listenersRef.current.forEach(l => l(jobId, jobsRef.current[jobId]));
      }
    } catch { /* skip tick */ }
  };

  // ponytail: single interval polls all active jobs; stable dep = never recreated
  useEffect(() => {
    const id = setInterval(() => {
      Object.values(jobsRef.current).forEach(j => pollJob(j.jobId));
    }, 3000);
    return () => clearInterval(id);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const registerJob = (jobId, stelleId, kind) => {
    const prev = jobsRef.current[jobId];
    const newStatus = kind === 'generierung'
      ? 'generiert'
      : (prev?.status && prev.status !== 'fertig' && prev.status !== 'fehler' ? prev.status : 'analyse');

    jobsRef.current = {
      ...jobsRef.current,
      [jobId]: {
        jobId,
        stelleId,
        kind,
        status: newStatus,
        schritt: prev?.schritt || '',
        analyse: prev?.analyse || null,
        analyse_struktur: prev?.analyse_struktur || null,
        fehler: kind === 'generierung' ? null : (prev?.fehler || null),
        jobtitel: prev?.jobtitel || null,
      },
    };
    sync();
    pollJob(jobId);
  };

  const removeJob = (jobId) => {
    const next = { ...jobsRef.current };
    delete next[jobId];
    jobsRef.current = next;
    sync();
  };

  const onAnyJobDone = (cb) => {
    listenersRef.current.push(cb);
    return () => { listenersRef.current = listenersRef.current.filter(l => l !== cb); };
  };

  return (
    <Ctx.Provider value={{ jobs, registerJob, removeJob, onAnyJobDone }}>
      {children}
    </Ctx.Provider>
  );
}

export const useJobs = () => useContext(Ctx);

export function useJobForStelle(stelleId) {
  const { jobs } = useContext(Ctx);
  if (!stelleId || !jobs) return null;
  return Object.values(jobs).find(
    j => j.stelleId === stelleId && j.status !== 'fertig'
  ) || null;
}
